// src/utils/authUtils.js
import { signOut as firebaseSignOut } from "firebase/auth";
import { firebaseAuth } from "src/firebase";
import { rawAPICall, getAPIHandler } from "src/Apiconfig/service";
import ApiConfig from "src/Apiconfig/config";
import axios from "axios";
import { toast } from "react-hot-toast";

/**
 * Wait for Firebase Auth to initialize
 * @returns {Promise<User|null>} Firebase user object or null
 */
export const waitForAuthInit = (timeoutMs = 8000) => {
  return new Promise((resolve) => {
    let settled = false;
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(user);
    });

    // onAuthStateChanged is expected to fire within milliseconds even for a logged-out visitor -
    // it resolves from local IndexedDB persistence, no network round-trip needed. If it hasn't
    // fired by this timeout, something's wrong with the SDK/browser storage rather than a user
    // genuinely still waiting (confirmed live: /secure/login and /secure/signup were hanging
    // blank indefinitely for every logged-out visitor, blocking all new signups and logins).
    // Fail OPEN here - treat as logged-out so LoginRedirectGuard shows the form - rather than
    // leaving the page permanently blank. A real logged-in user who hits this edge case just
    // sees the login screen instead of an instant redirect, which is recoverable; a blank page
    // that never resolves is not. Deliberately not unsubscribing on timeout, so a late real
    // resolution can still land (settled guards against double-resolving).
    setTimeout(() => {
      if (settled) return;
      settled = true;
      console.error(`⚠️ [waitForAuthInit] onAuthStateChanged did not fire within ${timeoutMs}ms - treating as logged-out`);
      resolve(null);
    }, timeoutMs);
  });
};

/**
 * Get current authentication state (hybrid approach)
 * @param {boolean} forceRefresh - Force backend verification instead of using cache
 * @returns {Promise<Object>} Auth state object
 */
export const getCurrentAuthState = async (forceRefresh = false) => {
  // 1. Wait for Firebase to initialize
  const user = await waitForAuthInit();
  
  if (!user) {
    return {
      isAuthenticated: false,
      hasProfile: false,
      needsOnboarding: false,
    };
  }

  // 2. Get fresh token from Firebase
  const token = await user.getIdToken();
  localStorage.setItem("accessToken", token);
  localStorage.setItem("userId", user.uid);

  // 3. Check profile (hybrid approach)
  const cachedProfile = localStorage.getItem("userProfileInfo");
  let hasProfile = false;

  // If we have a cached profile, consider it "complete" only when required
  // onboarding fields exist (prevents treating partial/skeleton profiles as done)
  if (cachedProfile && cachedProfile !== "null") {
    try {
      const parsed = JSON.parse(cachedProfile);
      hasProfile = Boolean(parsed?.currency && (parsed?.name || parsed?.acronym));
    } catch (e) {
      hasProfile = false;
    }
  }

  // 4. Optionally verify with backend (only if forced or no good cache)
  if (forceRefresh || !hasProfile) {
    try {
      console.log("🔍 [AuthUtils] Verifying profile with backend...");
      
      const response = await rawAPICall({
        method: "GET",
        url: `${ApiConfig["userProfile"]}`,
        headers: { Authorization: token },
      });
      
      if (response?.status === 200) {
        // Treat profile as "present" only when backend returns the fields
        const { homeCountry, preferredCurrency, avatarName } = response.data || {};
        if (homeCountry && preferredCurrency) {
          hasProfile = true;

          // ✅ Update cache with fresh, complete data
          try {
            const countryResponse = await getAPIHandler({ endPoint: "country", token });
            const enabledCountries = (countryResponse?.data || []).filter(i => i.enabled);
            const matched = enabledCountries.find(item => item.currency === preferredCurrency);

            const profileInfo = {
              name: homeCountry,
              currency: preferredCurrency,
              symbol: matched?.symbol || "",
              isEnabled: true,
              acronym: matched?.acronym || homeCountry,
              username: avatarName || "",
            };

            localStorage.setItem("userProfileInfo", JSON.stringify(profileInfo));
            console.log("✅ [AuthUtils] Profile cache updated (complete)");
          } catch (countryError) {
            console.error("⚠️ [AuthUtils] Failed to fetch country data:", countryError);
            const basicProfile = { name: homeCountry, currency: preferredCurrency, username: avatarName };
            localStorage.setItem("userProfileInfo", JSON.stringify(basicProfile));
          }
        } else {
          // Backend returned a skeleton/partial profile — treat as not onboarded
          hasProfile = false;
          localStorage.removeItem("userProfileInfo");
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("📋 [AuthUtils] Profile not found - user needs onboarding");
        hasProfile = false;
        localStorage.removeItem("userProfileInfo");
      } else {
        console.error("❌ [AuthUtils] Error checking profile:", error);
        // On other errors, keep previous (parsed) cache decision
        // (do not accidentally mark user as onboarded)
      }
    }
  }

  return {
    isAuthenticated: true,
    hasProfile,
    needsOnboarding: !hasProfile,
    user,
  };
};

/**
 * Centralized logout function
 * @param {Object} authContext - Auth context object
 */
export const handleLogout = async (authContext) => {
  try {
    // alert("22222")
    console.log("🔓 [Logout] Starting logout process...");
    const savedTheme = localStorage.getItem("theme");
    await firebaseSignOut(firebaseAuth);
    console.log("✅ [Logout] Firebase signout successful");
    if (authContext) {
        // alert("33333")
      authContext.userLogIn(false, "");
      authContext.setUserAddedWallets([]);
      authContext.setUserData(null);
      authContext.setUserProfileInfo(null);
    }
    localStorage.clear();
    sessionStorage.clear();
    if (savedTheme) {
      localStorage.setItem("theme", savedTheme);
    }
    // alert("44444")
    delete axios.defaults.headers.common.Authorization;
    
    console.log("✅ [Logout] Cleanup complete");
    // alert("55555")
    // window.location.href = "https://beta.blockstats.app/";
    window.location.href = "/secure/login";
    setTimeout(() => {
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, "", window.location.href);
      };
    }, 100);
    
  } catch (error) {
    console.error("❌ [Logout] Error during logout:", error);
    
    // Force cleanup even if Firebase signout fails
    try {
        // alert("66666")
      const savedTheme = localStorage.getItem("theme");
      localStorage.clear();
      sessionStorage.clear();
      if (savedTheme) {
        localStorage.setItem("theme", savedTheme);
      }
    //   alert("77777")
      delete axios.defaults.headers.common.Authorization;
    } catch (cleanupError) {
      console.error("❌ [Logout] Cleanup also failed:", cleanupError);
    }
    // alert("88888")
    // Still redirect even if there's an error
    // window.location.href = "https://beta.blockstats.app/";
    window.location.href = "/secure/login";
    toast.error("Logout encountered an issue, but you've been signed out.");
  }
};